
/**
 * PersonalDataCategory Service Layer
 * Rule: Business logic, validation, and API orchestration
 * Rule: NO UI IN SDK
 */
import { ApiError } from '../errors/api-error.js';
import { logDropdownParamsInvocation } from '../utils/dropdown-params-telemetry.js';
import { withRetry } from '../utils/retry.js';

export class PersonalDataCategoryService {
    constructor(api, config = {}) {
        this.api = api;
        this.config = config;
    }

    /**
     * Get all items with pagination, sorting, and search
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>}
     */
    async getAll(params = {}) {
        try {
            const response = await withRetry(
                () => this.api.getAll(params),
                { maxRetries: 2 }
            );

            // BaseApi unwrap check (usually handled by client, but let's be safe as per blueprint)
            const body = response;

            return {
                success: true,
                data: body.data || [],
                pagination: body.pagination || {},
                uniqueValues: body.uniqueValues || {},
                message: body.message || 'PersonalDataCategory listesi getirildi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch personal-data-category list');
        }
    }

    /**
     * Get item by ID
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async getById(id) {
        try {
            const response = await withRetry(
                () => this.api.getById(id),
                { maxRetries: 2 }
            );

            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch personal-data-category');
        }
    }

    /**
     * Create new item
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async create(data) {
        try {
            const response = await this.api.create(data);

            return {
                success: true,
                data: response.data,
                message: response.message || 'PersonalDataCategory oluşturuldu'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to create personal-data-category');
        }
    }

    /**
     * Update item
     * @param {number|string} id
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async update(id, data) {
        try {
            const response = await this.api.update(id, data);

            return {
                success: true,
                data: response.data,
                message: response.message || 'PersonalDataCategory güncellendi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to update personal-data-category');
        }
    }

    /**
     * Delete item
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async delete(id) {
        try {
            const response = await this.api.delete(id);

            return {
                success: true,
                data: response.data,
                message: response.message || 'PersonalDataCategory silindi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to delete personal-data-category');
        }
    }

    /**
     * Bulk delete items
     * @param {Array<number|string>} ids
     * @returns {Promise<Object>}
     */
    async bulkDelete(ids) {
        try {
            const response = await this.api.batchDelete(ids);
            return {
                success: true,
                data: response.data,
                message: response.message || `${ids.length} öğe silindi`
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to bulk delete personal-data-category');
        }
    }

    /**
     * Get dropdown parameters
     * @returns {Promise<Object>}
     */
    async getDropdownParams() {
        logDropdownParamsInvocation('PersonalDataCategoryService');
        try {
            const response = await this.api.getDropdown();
            return {
                success: true,
                data: response.data || [],
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch dropdown params');
        }
    }

    /**
     * Handle and transform errors
     */
    _handleError(error, defaultMessage) {
        if (error instanceof ApiError) {
            throw error;
        }

        if (error.statusCode || error.status) {
            throw ApiError.fromResponse(error);
        }

        throw new ApiError(error.message || defaultMessage, 500, 'UNKNOWN_ERROR', error);
    }
}
